/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { ButtonProps } from "@insite/mobius/Button";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xBaseTheme } from "@stoneage/XDL/types";
import { css } from "styled-components";

type BWT = Pick<ButtonProps, "shadow"> &
    InjectableCss & {
        _sizeVariant: ButtonProps["sizeVariant"];
        _size: ButtonProps["size"] | null;
        _color: ButtonProps["color"];
        _shape: ButtonProps["shape"];
    };

const buttonSizeVariants = {
    small: {
        height: 29,
        fontSize: 12,
        weight: 700,
        letterSpacing: 0,
        lineHeight: 13,
        padding: "1px 14px",
        icon: 12,
        iconPadding: 5,
        iconMargin: 5,
    },
    medium: {
        height: 34,
        fontSize: 14,
        weight: 700,
        letterSpacing: 0,
        lineHeight: 16,
        padding: "1px 21px",
        icon: 15,
        iconPadding: 5,
        iconMargin: 4,
    },
    large: {
        height: 53,
        fontSize: 18,
        weight: 700,
        letterSpacing: 0,
        lineHeight: 20,
        padding: "10px 28px",
        icon: 20,
        iconPadding: 5,
        iconMargin: 3,
    },
};

export const buttonTransparent = css`
    &,
    &:focus:not([disabled]),
    &:active:not([disabled]),
    &:disabled,
    &:focus:hover,
    &:hover:not(:disabled) {
        border: 0;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        transform: translate(0, 0);
        min-height: initial;
    }
`;

const buttonPresentationStyle = (buttonType: string) => {
    return {
        ...ButtonPresentationPropsDefault,
        buttonType: "solid",
        shape: "rectangle",
        sizeVariant: "medium",
        margeCss: true,
        css: css`
            &:focus,
            &:active {
                box-shadow: 0 0 5px 0 ${getColor("common.black")};
            }
            &:disabled {
                opacity: 0.33;
                box-shadow: none;
            }
            &,
            &:disabled,
            &:focus,
            &:active {
                && {
                    background: ${buttonType === "primary" ? getColor("primary.main") : getColor("common.white")};
                    color: ${buttonType === "primary" ? getColor("common.white") : getColor("primary.main")};

                    svg {
                        color: ${buttonType === "primary" ? getColor("common.white") : getColor("primary.main")};
                    }
                }
            }
            display: flex;
            align-items: center;
            outline: none !important;
            height: auto;
            min-height: ${(props: BWT) =>
                props["_size"] || get(buttonSizeVariants, [props["_sizeVariant"], "height"])}px;
            padding: ${({ _sizeVariant }) => get(buttonSizeVariants, [_sizeVariant, "padding"]) || "0 1em"};

            text-shadow: none;
            letter-spacing: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "letterSpacing"])}px;
            border: 1px solid ${getColor("primary")};
            border-radius: 4px;
            &,
            * {
                font-size: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "fontSize"])}px;
                font-weight: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "weight"])};
                line-height: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "lineHeight"])}px;
                text-transform: capitalize;
            }

            [class*="IconWrapper"] {
                margin-bottom: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "iconMargin"])}px;
                &,
                svg {
                    width: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "icon"])}px;
                    height: ${(props: BWT) => get(buttonSizeVariants, [props["_sizeVariant"], "icon"])}px;
                }
            }
        `,
    };
};

export default {
    primary: {
        ...buttonPresentationStyle("primary"),
        css: [
            buttonPresentationStyle("primary").css,
            css`
                &:hover:not(:disabled) {
                    color: ${getColor("primary")};
                    background: ${getColor("common.white")};
                    border-color: ${getColor("primary")};
                }
                &:hover svg {
                    color: ${getColor("primary")};
                    background: transparent;
                }
            `,
        ],
    },
    secondary: {
        ...buttonPresentationStyle("secondary"),
        css: [
            buttonPresentationStyle("secondary").css,
            css`
                &:hover:not(:disabled) {
                    color: ${getColor("common.white")};
                    background: ${getColor("primary")};
                    border-color: ${getColor("primary")};
                }
                &:hover svg {
                    color: ${getColor("common.white")};
                    background: transparent;
                }
            `,
        ],
    },
    tertiary: {
        ...buttonPresentationStyle("secondary"),
        css: [
            buttonPresentationStyle("secondary").css,
            css`
                &:hover:not(:disabled) {
                    color: ${getColor("common.white")};
                    background: ${getColor("primary")};
                    border-color: ${getColor("primary")};
                }
                &:hover svg {
                    color: ${getColor("common.white")};
                    background: transparent;
                }
            `,
        ],
    },
} as xBaseTheme["button"];
